import { useEffect } from 'react';
import type { TFunction } from 'i18next';
import { PackageCheck } from 'lucide-react';
import type { GridApplicability, GridApplicabilityField, PricingRequest } from '@carrierbridge/shared';
import { Select } from '@/components/ui/Select';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { usePricingCriteria } from '@/hooks/usePricing';
import { useMeansName } from '@/hooks/useMeans';
import { cn } from '@/lib/utils';
import {
  criterionViews,
  reconcileCriteria,
  type CriterionView,
  type ShipmentCriteria,
  type TriState,
} from './shipment-criteria';

const NO_SCOPES: GridApplicability[] = [];
const BOOLEAN_FIELDS: readonly GridApplicabilityField[] = ['dangerousGoods', 'temperatureControlled'];

/**
 * Ce que la marchandise a de particulier — type, flux, incoterm… — pour que les
 * grilles restreintes à ces valeurs puissent coter.
 *
 * Le bloc n'existe que s'il y a un choix à faire : il ne propose que les
 * critères qu'au moins une grille capable de coter ce trajet restreint, avec
 * pour valeurs celles que ces grilles acceptent. « Indifférent » n'y figure que
 * si l'une d'elles n'est pas restreinte sur ce critère ; sinon le critère est
 * obligatoire, et marqué comme tel. Choisir une valeur ajuste les autres.
 *
 * Il est volontairement en vue, hors de l'accordéon des précisions : sans lui,
 * les grilles restreintes ne cotent pas, et rien à l'écran ne dit pourquoi.
 */
export function QuoteShipmentCriteria({
  t,
  request,
  criteria,
  onChange,
}: {
  t: TFunction;
  /** Cotation sans critères ; nulle tant que le trajet n'est pas complet. */
  request: PricingRequest | null;
  criteria: ShipmentCriteria;
  onChange: (next: ShipmentCriteria) => void;
}) {
  const { data } = usePricingCriteria(request);
  const scopes = data?.scopes ?? NO_SCOPES;

  // Les grilles viennent de changer (trajet, date, quantité modifiés) : ce qui
  // était choisi n'est peut-être plus permis, ou n'a plus qu'une issue.
  useEffect(() => {
    const next = reconcileCriteria(scopes, criteria);
    if (next !== criteria) onChange(next);
  }, [scopes, criteria, onChange]);

  const views = criterionViews(scopes, criteria).filter((v) => v.visible);
  if (views.length === 0) return null;

  const anyRequired = views.some((v) => v.required);
  const choose = (field: GridApplicabilityField, value: string) =>
    onChange(reconcileCriteria(scopes, { ...criteria, [field]: value } as ShipmentCriteria));

  const textViews = views.filter((v) => !BOOLEAN_FIELDS.includes(v.field));
  const booleanViews = views.filter((v) => BOOLEAN_FIELDS.includes(v.field));

  return (
    <section
      aria-label={t('quote.shipmentCriteria')}
      className="space-y-3 rounded border border-accent/40 bg-accent-soft/40 p-3.5"
    >
      <header className="flex items-start gap-2">
        <PackageCheck className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden="true" />
        <div className="min-w-0">
          <p className="text-[13px] font-semibold text-text">{t('quote.shipmentCriteria')}</p>
          <p className="mt-0.5 text-2xs leading-snug text-muted">
            {anyRequired ? t('quote.shipmentCriteriaRequiredHint') : t('quote.shipmentCriteriaHint')}
          </p>
        </div>
      </header>

      {textViews.length > 0 && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {textViews.map((view) => (
            <CriterionSelect
              key={view.field}
              t={t}
              view={view}
              value={criteria[view.field]}
              onChange={(value) => choose(view.field, value)}
            />
          ))}
        </div>
      )}

      {booleanViews.map((view) => (
        <CriterionToggle
          key={view.field}
          t={t}
          view={view}
          value={criteria[view.field] as TriState}
          onChange={(value) => choose(view.field, value)}
        />
      ))}
    </section>
  );
}

function CriterionSelect({
  t,
  view,
  value,
  onChange,
}: {
  t: TFunction;
  view: CriterionView;
  value: string;
  onChange: (value: string) => void;
}) {
  const meansName = useMeansName();
  // Valeur choisie que la liste n'offre plus (le temps d'un rendu, avant
  // l'ajustement) : gardée affichée plutôt que remplacée par la première.
  const values = value && !view.values.includes(value) ? [value, ...view.values] : view.values;
  // Le moyen se choisit par son nom mais part au moteur par son code : la
  // valeur de l'option reste le code, seul le libellé change.
  const label = (v: string) => (view.field === 'means' ? meansName(v) : v);
  const options = values.map((v) => ({ value: v, label: label(v) }));
  if (view.field === 'means') {
    options.sort((a, b) => a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' }));
  }
  return (
    <Select
      label={t(`gridApplicability.fields.${view.field}`)}
      required={view.required}
      placeholder={view.allowAny ? t('quote.criteriaAny') : t('quote.criteriaChoose')}
      options={options}
      value={value}
      className={cn(view.required && !value && 'border-accent ring-1 ring-accent/40')}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function CriterionToggle({
  t,
  view,
  value,
  onChange,
}: {
  t: TFunction;
  view: CriterionView;
  value: TriState;
  onChange: (value: TriState) => void;
}) {
  const label = t(`gridApplicability.fields.${view.field}`);
  const options: { value: TriState; label: string }[] = [
    ...(view.allowAny ? [{ value: '' as TriState, label: t('quote.criteriaAny') }] : []),
    ...view.values.map((v) => ({
      value: v as TriState,
      label: v === 'true' ? t('common.yes') : t('common.no'),
    })),
  ];
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="whitespace-nowrap text-[13px] font-medium text-text">
        {label}
        {view.required && <span aria-hidden="true" className="ml-0.5 text-danger">*</span>}
      </span>
      <SegmentedControl size="sm" aria-label={label} value={value} onChange={(v) => onChange(v as TriState)} options={options} />
    </div>
  );
}
